/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {initReactI18next} from 'react-i18next';
import i18n, {InitOptions, TFunction} from 'i18next';
import Fetch from 'i18next-fetch-backend';
import {applyClientHMR} from 'i18next-hmr/client';
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import moment from 'moment';
import {getLocalStorage} from '../capabilities/storage';
import {get} from '../environment/environment';
import getVersion from '../environment/version';
import {info} from '../logger/logger';
import {Preference} from '../types/preferences';
import {isTrue} from '../utils/BooleanUtils';
import {dedupeChars} from '../utils/StringUtil';

/**
 * Defines the default path against which all requests to load translation
 * documents will be resolved.
 */
export const defaultLoadPath = `/locales/{{lng}}/{{ns}}.json?v=${getVersion()}`;

/**
 * Defines the white listed locale which are supported by the majority of
 * CalAmp Applications.
 */
export enum WhitelistedLocales {
  en = 'en',
  'en-GB' = 'en-GB',
  es = 'es',
  'es-ES' = 'es-ES',
  'es-MX' = 'es-MX',
  it = 'it',
  de = 'de',
  fr = 'fr',
  nl = 'nl',
  'pt-BR' = 'pt-BR'
};

/**
 * Provides a reference to the optional overrides namespace to be used when
 * resolving entries.
 *
 * @see {@link translateOverride}
 * @see {@link initializeLanguageAsync}
 */
let _overrideNamespace = null;

/**
 * The function returns language key that accepted by server or client for
 * some special language key.
 *
 * ```typescript
 *
 *  import {getLanguageKey} from '@ion/core/src/locale/locale';
 *
 *  getLanguageKey('ja'); // ja
 *  getLanguageKey('en'); // en
 *  getLanguageKey('en_GB'); // en-GB
 *  getLanguageKey('en-GB'); // en_GB
 *
 * ```
 *
 * @param language The language key to the language.
 * @returns Server/Client acceptable language key.
 */
export const getLanguageKey = (language: string) => {
  const lang = {
    default : null,
    'en-GB' : 'en_GB',
    en_GB   : 'en-GB',
    'es-ES' : 'es_ES',
    es_ES   : 'es-ES',
    'es-MX' : 'es_MX',
    es_MX   : 'es-MX',
    'pt-BR' : 'pt_BR',
    pt_BR   : 'pt-BR'
  };
  return !isUndefined(lang[language]) ? lang[language] : WhitelistedLocales[language];
};

/**
 * The function returns the language respect to the language key.
 *
 * ```typescript
 *
 *  import {getLanguageKey} from '@ion/core/src/locale/locale';
 *
 *  getLanguageByKey('ja'); // Japanese
 *  getLanguageByKey('en'); // English
 *  getLanguageByKey('en_GB'); // English(United Kingdom)
 *
 * ```
 *
 * @param langKey The language key to the language.
 * @returns User readable language.
 */
export const getLanguageByKey = (langKey: string) => {
  const languageMap = translate('common:languages', {returnObjects: true});
  langKey           = getLanguageKey(langKey);
  return languageMap?.[langKey] || langKey || languageMap?.default;
};

export const initializeLanguage = () => {
  switchLanguage(getDefaultLanguage()).then(() => {
    info(`User Default Language Configured [${getLanguage()?.toUpperCase()}]`);
  });
};

/**
 * Defines an async utility to initialize the default language
 *
 * ```typescript
 *   import {initializeLanguageAsync} from '@ion/core/src/locale/locale';
 *
 *   await initializeLanguageAsync();
 * ```
 *
 * @param  lang Optional initial language against which the translations are to be
 *         initialized.
 * @param  namespace an optional namespace which is to be used to provide overrides
 *         for translation entries. This parameter is generally used for implementing
 *         co-branded translation overrides.
 */
export const initializeLanguageAsync = async(lang?: string, namespace = null) => {
  const result = await switchLanguage(lang || getDefaultLanguage());
  info(`User Default Language Configured [${getLanguage()?.toUpperCase()}]`);
  _overrideNamespace = namespace;
  return result;
};

/**
 * Provides a utility which applies HMR support; thus allowing for hot reloading
 * translation entry changes without the need to explicitly reload the application.
 *
 * ```typescript
 *   import {localeHMR} from '@ion/core/src/locale/locale';
 *
 *   // if this is the local development build, enable HMR ...
 *   process.env.NEXT_PUBLIC_IS_DEV && localeHMR();
 *
 *   // or validate automatically ...
 *   localeHMR(true);
 * ```
 *
 */
export const localeHMR = (useNext = false) => {
  const valid = useNext ? isTrue(process.env.NEXT_PUBLIC_IS_DEV) : true;
  valid && applyClientHMR(i18n);
};

/**
 * Given a string path to a localized resource containing optional arguments,
 * returns the translated resource.
 *
 * ```typescript
 *   import {translate} from '@ion/core/src/locale/locale';
 *
 *   const username = translate('login:username');
 * ```
 *
 * @param  {string} path Path to the localized resource; e.g. 'login:username'
 * @return {string} Localized resource, if found, otherwise, default fallback,
 *         which is set to the provided resource path.
 */
export const translate = (path: string, options?: object): any => {
  if (i18n.isInitialized && !isNil(path)) {
    if (_overrideNamespace) {
      return translateOverride(path, options);
    }
    return i18n.t(path, options);
  }
  return path;
};

/**
 * Implements translation entry overriding for when an override `namespace`
 * is provided during the initialization call to `initializeLanguageAsync`.
 *
 * Overrides allow for defining translation entries to be used in place of
 * the default translation entries for a given namespace.
 *
 * For instance,  assume we have a "title" entry defined in `login.json`,
 * and this module is initialized with 'ups' as a namespace, then this
 * function will attempt to resolve the entry as `ups:login.title' from
 * the `ups.json` document's 'login.title' key. If the value can be resolved
 * (meaning, an override for the given key has been provided), then it will
 * be returned, otherwise the default value from 'login:title' will be
 * returned. This functionality essentially allows for overriding specific
 * translation entries by defining the exact same paths in the overridden
 * translation.json document as needed, with the guarantee that if the
 * value has not been overridden, then the default value will be used.
 *
 * Note: This function should not be made public, and is only to be invoked
 * directly from the `translate` function.
 *
 * ```typescript
 *   // initialize the module with 'assistcargo' as an override namespace ...
 *   initializeLanguageAsync(null, 'assistcargo');
 *
 *   ...
 *
 *   // when invoking `translate`, if an override namespace has been provided
 *   // during initialization, the API will first determine if the given
 *   // path is defined within the 'assistcargo.json' override module, and
 *   // if so, the value will be returned, otherwise, it will return the
 *   // default value for the path ...
 *   translateOverride('login:placeholders.email');
 *
 *   // in the above call, if the `assistcargo.json` document provides a
 *   // translation entry for `login.placeholders.email`, it's value will
 *   // be returned, otherwise, the default value for `placeholders.email`
 *   // from the `login.json` module will be returned.
 *
 * ```
 *
 * @param  {string} path Path to the localized resource; e.g. 'login:title'
 * @param  {object} optional options object to be passed to the underlying api.
 * @return {string} overridden resource value, if found, otherwise, fallbacks
 *         to returning the value for the default resource.
 */
const translateOverride = (path: string, options?: object): any => {
  if (path) {
    const [module, key] = path.split(':');
    const source = `${module}.${key}`;
    const entry  = `${_overrideNamespace}:${source}`;
    const value  = i18n.t(entry, options);
    if (value !== source) {
      return value;
    }
  }
  return i18n.t(path, options);
};

/**
 * Returns the current language code for which the application is being localized.
 * This method simply provides a convenience and delegates to i18n.lng().
 *
 * When testing the implementation of localized resource and / or translations for
 * a specific language, developers are to change  their browser's default language,
 * or language order, to that of the language against which they are testing. For
 * information  on how to change the default language for specific browsers, please
 * see the following:
 *
 * <ul>
 *   <li>Chrome: http://bit.ly/2dSivqc</li>
 *   <li>Firefox: http://bit.ly/2dxZ8j3</li>
 *   <li>IE: http://bit.ly/2cT0jw0</li>
 * </ul>
 *
 * @param    useFramework When true (default), returns the language used by the
 *           localization framework. Otherwise, when false, returns the language
 *           based on window.navigator.
 * @returns  The current language for which the application is localized.
 */
export const getLanguage = (useFramework = true): string => {
  if (!useFramework) {
    if (!isUndefined(navigator.languages)) {
      return navigator.languages[0];
    }
    return navigator.language;
  }
  return i18n?.language || getLanguage(false);
};

/**
 * Provides a utility which allows for changing the locale in real-time.
 *
 * @param  {string} language The language to re-render the application against.
 */
export const switchLanguage = async(language: string): Promise<boolean | TFunction> => {
  const current = i18n.language;
  if (language !== current) {
    const updated = !language ? getLanguage(false) : language;
    moment.updateLocale(updated, {parentLocale: language});
    return i18n?.changeLanguage(updated, function(e) {
      info(`User Language changed from [${current}] to [${language}] ${translate('login:signin')}`);
    });
  }
  return Promise.resolve(false);
};

/**
 * Determines the default language against which the application translations
 * are to be initialized. The default language is resolved by first determining
 * if a 'defaultLanguage' is present in localStorage, if not, the current browser
 * locale is used (if supported), otherwise, the 'defaultLanguage' entry from
 * environment.json will be used as the fallback (generally, 'en');
 *
 * @returns The current default language detected, testing against localStorage,
 *          followed by the user agent, environment.json, and falling back to en
 *          if none of the above resulted in a detection.
 */
export const getDefaultLanguage = (): string => {
  return (
    getLocalStorage(Preference.defaultLanguage) ||
    parseLocale(getLanguage(false)) ||
    get(Preference.defaultLanguage) ||
    WhitelistedLocales.en
  );
};

/**
 * Given a specific locale (such as 'es-US'), parses the locale to it's whitelisted
 * representative locale. For instance,'es' is supported as a whitelisted locale, thus,
 * if the current locale is 'es-US', the local will be parsed and default to 'es'.
 *
 * ```typescript
 *
 *   // english whitelisted locale ...
 *   let locale = getLanguage(); // 'en'
 *   console.log(parseLocale(locale)); // 'en'
 *
 *   // english US whitelisted locale ...
 *   let locale = getLanguage(); // 'en-US'
 *   console.log(parseLocale(locale)); // 'en'
 *
 *   // english UK whitelisted locale ...
 *   let locale = getLanguage(); // 'en-GB'
 *   console.log(parseLocale(locale)); // 'en-GB'
 *
 *   // spanish whitelisted locale ...
 *   let locale = ; // 'es-US'
 *   console.log(parseLocale(locale)); // 'es'
 *
 *   // france (unsupported), non-whitelisted locale ...
 *   let locale = getLanguage(); // 'fr-CA'
 *   console.log(parseLocale(locale)); // undefined
 * ```
 *
 */
const parseLocale = (locale: string): string => {
  let [lang, region] = locale.split('-');
  lang = lang?.toLowerCase();
  region = region?.toUpperCase();
  let lng = WhitelistedLocales[[lang, region].join('-')];
  if (!lng) {
    lng = WhitelistedLocales[lang];
  }
  return lng;
};

/**
 * By default, translation document resources are loaded as per the default
 * backend.loadPath value which is set to '/locales/{{lng}}/{{ns}}.json';
 * This function allows for updating the url against which the translation
 * resources are resolved. This is especially important for Remote Components
 * exposed from Micro Frontend Container Applications which will reside in a
 * different domain when rendered within a host. As such, the `updateLoadPath`
 * function can be used to set the root URL for the given Remote Components
 * in order to ensure that the resources will be correctly resolved.
 *
 * The default value is set to '/locales/{{lng}}/{{ns}}.json' path, and by
 * default, this value will be appended to the provided `url`. To override
 * this behavior, and define the complete path from which translation files
 * are to be resolved, set the `includePath` argument to `false`; however,
 * when defining the fully qualified path from which translation documents
 * are to be resolves, the language token `{{lng}}` (if applicable), and
 * the file name token `{{ns}}.json` (namespace) must be included.
 *
 * ```typescript
 *   import {updateLoadPath} from '@ion/core/src/locale/locale';
 *   ...
 *
 *   updateLoadPath('https://app.calamp.com');
 *   // https://app.calamp.com/locales/{{lng}}/{{ns}}.json`
 *   ...
 *
 *   // set includePath argument to false to specify url and path ...
 *   updateLoadPath('https://app.calamp.com/i18n/{{lng}}/{{ns}}.json', false);
 *   // https://app.calamp.com/locales/{{lng}}/{{ns}}.json`
 * ```
 *
 * See: {@link defaultLoadPath}
 * @param   url Defines the root url from which all request to `/locales` are
 *          to be resolved.
 * @param   includePath When `true` (default), the `url` will be used as the
 *          domain against which the translation resources are resolved, and
 *          the default path '/locales/{{lng}}/{{ns}}.json' will be appended
 *          to the `url`. When `false`, the root url will be used as the full
 *          path against which the translation resources are resolved.
 * @returns `true` if the backend connectors loadPath has been successfully
 *          updated, otherwise, `false`.
 */
export const updateLoadPath = (url: string, includePath = true): boolean => {
  const connector = i18n.services.backendConnector;
  if (url && connector?.backend) {
    const updated   = includePath ? `${url}${dedupeChars(`/${defaultLoadPath}`)}` : url;
    const {options} = connector.backend;
    if (updated !== options.loadPath) {
      connector.backend.options.loadPath = updated;
    }
    return true;
  }
  return false;
};

/**
 * Provides a simple utility for referencing the current path / url from which
 * translation documents will be resolved. If the `updateLoadPath` function
 * has not been invoked, this value will always resolve to that of the APIs
 * `defaultLoadPath`.
 *
 * ```typescript
 *   import {getLoadPath} from '@ion/core/src/locale/locale';
 *   ...
 *
 *   getLoadPath();
 *   // '/locales/{{lng}}/{{ns}}.json'
 *   ...
 *
 *   updateLoadPath('http://app.calamp.com');
 *   ...
 *   getLoadPath();
 *   // 'http://app.calamp.com/locales/{{lng}}/{{ns}}.json'
 * ```
 *
 * {@link updateLoadPath}
 * {@link defaultLoadPath}
 *
 * @returns The current value of the underlying i18n Backend Connect loadPath.
 */
export const getLoadPath = (): string => {
  const connector = i18n.services.backendConnector;
  if (connector?.backend) {
    return connector.backend.options.loadPath;
  }
};

/**
 * Determines if the current application is the remote app (Vehicle Inspections).
 * This is based on the environment variable `NEXT_PUBLIC_APP_NAME`.
 */
const isRemoteApp = process.env.NEXT_PUBLIC_APP_NAME === 'Vehicle Inspections';

/**
 * Defines the default i18n configurations used by all application. Clients can
 * generally use the default configuration which is used by the default `i18n`
 * export; however, in certain cases the `initialize` function can be utilized
 * to provide a custom configuration.
 */
const defaultConfiguration = {
  supportedLngs : Object.values(WhitelistedLocales),
  fallbackLng   : WhitelistedLocales.en,
  load          : 'currentOnly',
  ns            : isRemoteApp ? [] : ['common', 'network'],
  defaultNS     : 'common',
  interpolation : {
    escapeValue : false
  },
  backend : {
    loadPath : defaultLoadPath
  },
  react : {
    useSuspense   : false,
    bindI18n      : 'loaded',
    bindI18nStore : 'languageChanged loaded added removed'
  }
};

/**
 * The locale module initializes the i18n framework with application specific
 * configurations, providing a default namespace "common", and a fallbackLng,
 * "en".
 */
i18n
  .use(Fetch)
  .use(initReactI18next)
  .init(defaultConfiguration as InitOptions);

// eslint-disable-next-line no-barrel-files/no-barrel-files
export default i18n;
